import React, { useEffect, useState, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { UtensilsCrossed } from 'lucide-react';
import { api } from '../services/api';
import CountdownTimer from './CountdownTimer';

const DAY_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];

const getTodayKey = () => {
  const idx = new Date().getDay();
  const map = { 1: 'monday', 2: 'tuesday', 3: 'wednesday', 4: 'thursday', 5: 'friday' };
  return map[idx] || 'monday';
};

const LunchMenu = () => {
  const { language, t } = useLanguage();
  const [activeDay, setActiveDay] = useState(getTodayKey());
  const [menuData, setMenuData] = useState({});
  const [weekNumber, setWeekNumber] = useState(null);
  const [activateAt, setActivateAt] = useState(null);
  const [serverTime, setServerTime] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMenu = useCallback(async () => {
    try {
      const data = await api.getMenu();
      setMenuData(data.current || {});
      setWeekNumber(data.current_week || null);
      setActivateAt(data.activate_at || null);
      setServerTime(data.server_time || null);
    } catch (e) {
      // keep prior menu on error
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMenu();
    const id = setInterval(fetchMenu, 30000); // poll every 30s
    return () => clearInterval(id);
  }, [fetchMenu]);

  const handleTimerComplete = useCallback(() => {
    // After timer expires, refetch to get updated menu
    setTimeout(fetchMenu, 1500);
  }, [fetchMenu]);

  const currentDishes = menuData[activeDay]?.dishes || [];

  return (
    <section id="lunch" className="py-24 bg-[#FAF6F0] relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-24 bg-gradient-to-b from-[#F0E9DC]/40 to-transparent pointer-events-none" />
      <div className="max-w-6xl mx-auto px-6 relative">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 text-[#B8934A] mb-4">
            <UtensilsCrossed size={18} />
            <span className="text-xs tracking-[0.3em] uppercase font-medium">Lounasbuffet</span>
          </div>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#1F2A24] mb-4 font-semibold">
            <span className="section-divider">{t.lunch.heading}</span>
          </h2>
          {weekNumber && (
            <p className="text-[#7A1F25] font-serif text-xl md:text-2xl font-medium mb-3">
              {t.lunch.week} {weekNumber}
            </p>
          )}
          <p className="text-[#6B6257] max-w-2xl mx-auto text-base md:text-lg leading-relaxed">
            {t.lunch.subheading}
          </p>
          <p className="text-[#B8934A] mt-3 text-sm tracking-wider uppercase">{t.lunch.servedDaily}</p>
        </div>

        <CountdownTimer activateAt={activateAt} serverTime={serverTime} onComplete={handleTimerComplete} />

        <div className="flex flex-wrap justify-center gap-2 md:gap-3 mb-12">
          {DAY_ORDER.map(day => (
            <button
              key={day}
              onClick={() => setActiveDay(day)}
              className={`px-5 md:px-7 py-2.5 rounded-full text-sm md:text-base font-medium tracking-wide transition-all ${
                activeDay === day
                  ? 'bg-[#7A1F25] text-white shadow-md'
                  : 'bg-white text-[#1F2A24] hover:bg-[#F0E9DC] border border-[#E8DFCF]'
              }`}
            >
              {t.lunch.days[day]}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-center text-[#6B6257] py-10">…</p>
        ) : currentDishes.length === 0 ? (
          <p className="text-center text-[#6B6257] py-10">{t.lunch.noMenu}</p>
        ) : (
          <div className="grid md:grid-cols-2 gap-5 md:gap-6">
            {currentDishes.map((dish, idx) => {
              const content = dish[language] || dish.fi || { name: '', description: '' };
              return (
                <div
                  key={idx}
                  className="bg-white border border-[#EDE4D2] rounded-xl p-6 hover:shadow-lg hover:-translate-y-0.5 transition-all group"
                >
                  <div className="flex items-start gap-4">
                    <div className="shrink-0 w-11 h-11 rounded-full bg-[#F4EDDC] flex items-center justify-center border border-[#E6D9B8] group-hover:bg-[#E8D9B0] transition-colors">
                      <span className="font-serif text-[#B8934A] font-semibold">{idx + 1}</span>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-serif text-xl md:text-2xl text-[#1F2A24] mb-2 font-semibold">
                        {content.name}
                      </h3>
                      <p className="text-[#6B6257] leading-relaxed text-sm md:text-base">
                        {content.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default LunchMenu;
